import { useTranslation } from "react-i18next";
import { Button, Modal, useDisclosure } from "@heroui/react";
import RedButton from "/app/src/components/HeroUi/RedButton";
import ModalBuilder from "/app/src/components/HeroUi/helpers/ModalBuilder";
import { useCallback } from "react";
import { IconBuilder } from "/app/src/components/icons/IconBuilder";

/**
 * Renders a popup component with a delete button and confirmation modal.
 *
 * @param {Object} props - The component props.
 * @param {() => Promise<any>} props.deleteObjectFn - Function to delete the object.
 * @param {() => void} props.refresh - Function to refresh the list after deletion.
 * @param {string} [props.headerText] - Optional header text for the modal.
 * @returns {JSX.Element} The DeleteButtonPopup component.
 */
export default function DeleteButtonPopup({
  deleteObjectFn,
  refresh,
  headerText,
  modalBody,
  onBeforeOpen,
}: {
  deleteObjectFn: () => Promise<any>;
  refresh: () => void;
  headerText?: string;
  modalBody?: React.ReactNode;
  onBeforeOpen?: () => void;
}) {
  const { t } = useTranslation();
  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  const handleOpen = useCallback(() => {
    onBeforeOpen?.();
    onOpen();
  }, [onBeforeOpen, onOpen]);

  const deleteObject = useCallback(() => {
    deleteObjectFn().then((response) => {
      // Refresh the list of reports if channel successfully removed
      if (response.deleted === "true") {
        refresh();
      }
    });
  }, [deleteObjectFn, refresh]);

  return (
    <>
      <button
        onClick={handleOpen}
        onKeyDown={handleOpen}
        className="bg-transparent border-0"
      >
        <IconBuilder icon="Delete" color="#B5B5B5" />
      </button>
      <Modal
        disableAnimation={false}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        placement="top"
        aria-label="Confirm delete"
      >
        {ModalBuilder({
          warning: true,
          modalHeader: headerText,
          modalBody,
          modalFooter: (
            <>
              <Button
                size="sm"
                radius="none"
                color="default"
                variant="bordered"
                onPress={onOpenChange}
                className="hover:border-primary-default hover:text-primary-default"
              >
                {t("translation:do_not_delete")}
              </Button>
              <RedButton radius="none" size="sm" onPress={deleteObject}>
                {t("translation:delete")}
              </RedButton>
            </>
          ),
        })}
      </Modal>
    </>
  );
}
